import { Pill, ClipboardList, Stethoscope, MessageCircleQuestion, FolderOpen } from 'lucide-react';
// The chart's own vocabulary, not a second copy of it. `chartEventsFor` below
// proposes a surface state, and a proposal spelled differently from what the
// tooth editor writes is a chart that disagrees with itself.
import {
  SURFACES, SURFACE_STATE, SURFACE_STATE_OPTIONS, CONDITION_OPTIONS, PRESENCE,
  surfacesFor,
} from '../../lib/teeth.js';
import { normalizeCurrency, CURRENCY_CODES } from '../../lib/money.js';

// جلسهٔ درمان — the visit workspace's whole contract, in one module.
//
// Everything BOTH surfaces have to agree about lives here: the left column's
// tab set, every GraphQL document, and the pure derivations. The two screens may
// look nothing alike and must never disagree about what a tab IS or what a
// query asks for — the README's "two surfaces, one behaviour" rule, applied to
// the biggest screen in the app.
//
// dent-visit.md §2 fixes the layout:
//
//   RIGHT (persistent)  odontogram · subjective · طرح درمان — always in view,
//                       "so the clinician always has the patient's dental
//                       context in view"
//   LEFT  (dynamic)     تشخیص · دارو · دستورات · پرسش · فایل‌ها, one viewport
//                       that swaps on the active tab
//
// On mobile the two columns become two SECTIONS of one scroll rather than two
// panes — §2's "persistent" is about the clinical context never being more than
// a glance away, which a phone honours by keeping the odontogram at the top and
// the tab strip sticky, not by splitting a 390px viewport in half.

// ── The left column's tabs ──────────────────────────────────────────────────
//
// The values ARE the `?tab=` query parameter, so renaming one is a URL change.
// §2 lists them دارو, دستورات, تشخیص, پرسش, فایل ها, but تشخیص leads: the
// diagnosis is what the rest of the visit hangs off — a drug, an order and a
// treatment-plan item all follow FROM it — so it is the pane the visit opens on
// and the one the URL omits.
export const VISIT_TABS = (t) => [
  { value: 'diagnosis', label: t('visit.tabs.diagnosis'), Icon: Stethoscope },
  { value: 'meds', label: t('visit.tabs.meds'), Icon: Pill },
  { value: 'orders', label: t('visit.tabs.orders'), Icon: ClipboardList },
  { value: 'questions', label: t('visit.tabs.questions'), Icon: MessageCircleQuestion },
  { value: 'files', label: t('visit.tabs.files'), Icon: FolderOpen },
];

/** The tab ids, without needing a translator. index.jsx's URL whitelist. */
export const VISIT_TAB_IDS = ['diagnosis', 'meds', 'orders', 'questions', 'files'];

/** The tab that is omitted from the URL, and the fallback for an unknown one. */
export const DEFAULT_VISIT_TAB = 'diagnosis';

// ── The visit header ────────────────────────────────────────────────────────
//
// ONE query for the appointment AND its patient, deliberately: the patient id
// every other section scopes on comes from the SERVER's view of this
// appointment, never from the URL. A screen that reads the patient from the
// path and the visit from the id can be made to paint one patient's chart under
// another patient's name by editing the address bar.
// «پایان جلسه». One-way: after this the visit is read-only and nothing in this
// app reopens it, so the screen must flush unsaved work BEFORE calling it and
// must not call it when that flush failed.
// `followUp` is what the dentist answered about «نوبت بعدی» — omitted entirely
// by a close that did not ask. Saying nothing is NOT «لازم نیست»: the work is
// outstanding either way, and the desk is told with the default interval.
export const COMPLETE_VISIT = `
  mutation CompleteVisit($patientId: ID!, $appointmentId: ID!, $followUp: VisitFollowUpInput) {
    completeVisit(patientId: $patientId, appointmentId: $appointmentId, followUp: $followUp) {
      appointmentId
      status
      editable
      followUpOutcome
    }
  }
`;

export const VISIT_QUERY = `
  query Visit($appointmentId: ID!) {
    visit(appointmentId: $appointmentId) {
      appointmentId
      patientId
      clinicId
      dateTime
      status
      urgency
      durationMinutes
      editable
      patient {
        id name phone nationalId birthDate age gender bloodType fileNumber avatarUrl
        allergies { id allergen reaction severity }
        conditions { id code display clinicalStatus onsetDate }
        medications { id code display status authoredOn dosageInstruction }
      }
    }
  }
`;

// ── ویزیت تصویری ────────────────────────────────────────────────────────────
//
// One query, run TWICE and for two different reasons: once to decide whether
// «تماس تصویری» is drawn at all, and again at the moment the dentist presses it.
// It is not cached between the two, and that is the point — the token is short-
// lived, and re-running the query is also what re-checks that this visit is
// still open and still this dentist's.
//
// It returns null (not an error) when the deployment has no media server, which
// is what lets the button simply not exist rather than exist and fail.
export const VIDEO_VISIT_SESSION_QUERY = `
  query VideoVisitSession($patientId: ID!, $appointmentId: ID!) {
    videoVisitSession(patientId: $patientId, appointmentId: $appointmentId) {
      domain
      roomName
      token
      expiresAt
      displayName
      moderator
    }
  }
`;

// ── Right column: the subjective ────────────────────────────────────────────

const NOTE_FIELDS = `
  id
  appointmentId
  createdAt
  subjective
  objective
  assessment
  plan
  dental {
    chiefComplaint painScore painCharacter teethFdi
    onset duration aggravatingFactors relievingFactors dentalHistory notes
  }
`;

export const VISIT_NOTE_QUERY = `
  query VisitNote($patientId: ID!, $appointmentId: ID!) {
    visitNote(patientId: $patientId, appointmentId: $appointmentId) { ${NOTE_FIELDS} }
  }
`;

export const SAVE_VISIT_NOTE = `
  mutation SaveVisitNote(
    $patientId: ID!, $appointmentId: ID!,
    $subjective: DentalSubjectiveInput, $objective: String, $assessment: String, $plan: String
  ) {
    saveVisitNote(
      patientId: $patientId, appointmentId: $appointmentId,
      subjective: $subjective, objective: $objective, assessment: $assessment, plan: $plan
    ) { ${NOTE_FIELDS} }
  }
`;

export const PRIOR_VISITS_QUERY = `
  query PriorVisits($patientId: ID!, $appointmentId: ID!, $limit: Int) {
    priorVisits(patientId: $patientId, appointmentId: $appointmentId, limit: $limit) {
      appointmentId dateTime summary diagnosisCount hasNote
    }
  }
`;

// ── Right column: طرح درمان ─────────────────────────────────────────────────
//
// The dental service's plan, not a second model of one. Item status is the
// service's vocabulary; PLAN_ITEM_STATUS below maps it to the words §3.1.3 uses.
//
// Every `…Rial` amount is an integer number of MINOR UNITS of the plan's
// `currency` (whole rials for IRR — the names are historical and kept). Items
// carry no currency of their own: a plan is ONE currency, so the plan's is read.
const PLAN_FIELDS = `
  id
  clinicId
  title
  status
  approvedAtMs
  note
  totalAmountRial
  patientTotalRial
  insuranceTotalRial
  items {
    id phase catalogId toothFdi surfaces description status sortOrder quantity
    lineAmountRial linePatientRial
  }
`;

// What the plan READ asks for: the fields above plus what every amount is
// counted in. SIGNED, that is the currency stamped at approval; UNSIGNED, the
// plan clinic's current one — which the BFF reads from clinicmanagement.
//
// Only the read asks for it. The mutations below discard their response (the
// hook refetches after every write), and a write that SUCCEEDED must not come
// back as a failure because a display-only field could not be resolved: a
// createTreatmentPlan reported as failed is one the dentist presses again, and
// that makes a second plan.
const PLAN_READ_FIELDS = `
  ${PLAN_FIELDS}
  currency
`;

// What a plan LINE may name. Deliberately NOT the order catalogue: an order is
// work sent to a lab or a radiology unit, a treatment is work the dentist does,
// and offering the one as the other quotes a radiograph as a treatment.
export const TREATMENT_CATALOG_QUERY = `
  query DentalTreatmentCatalog($clinicId: ID) {
    dentalTreatmentCatalog(clinicId: $clinicId) {
      id name category tariffRial currency durationMinutes applicability chartEffect archived source
    }
  }
`;

export const TREATMENT_PLANS_QUERY = `
  query TreatmentPlans($patientId: ID!, $clinicId: ID) {
    treatmentPlans(patientId: $patientId, clinicId: $clinicId) { ${PLAN_READ_FIELDS} }
  }
`;

export const CREATE_TREATMENT_PLAN = `
  mutation CreatePlan($patientId: ID!, $clinicId: ID, $title: String, $items: [TreatmentPlanItemInput!]) {
    createTreatmentPlan(patientId: $patientId, clinicId: $clinicId, title: $title, items: $items) {
      ${PLAN_FIELDS}
    }
  }
`;

export const UPDATE_TREATMENT_PLAN = `
  mutation UpdatePlan($planId: ID!, $items: [TreatmentPlanItemInput!]) {
    updateTreatmentPlan(planId: $planId, items: $items) { ${PLAN_FIELDS} }
  }
`;

export const SET_PLAN_ITEM_STATUS = `
  mutation SetPlanItemStatus($planId: ID!, $itemId: ID!, $status: String!) {
    setPlanItemStatus(planId: $planId, itemId: $itemId, status: $status) { ${PLAN_FIELDS} }
  }
`;

// «خدمت جدید» — a service invented at the chair, saved to the CLINIC.
//
// Returns the catalogue entry ready to be added to a plan, in the same shape
// the picker reads, so the new service can be selected immediately rather than
// after a refetch that may not have landed yet.
export const CREATE_CLINIC_SERVICE = `
  mutation CreateClinicService($clinicId: ID, $input: NewClinicServiceInput!) {
    createClinicService(clinicId: $clinicId, input: $input) {
      id name category tariffRial currency durationMinutes applicability chartEffect archived source
    }
  }
`;

// THE LOCK. No amounts: they are read from the clinic's own declarations, in
// the BFF, at the moment of signing. A client that could name them could name
// a number onto a document a patient puts their signature to.
export const APPROVE_TREATMENT_PLAN = `
  mutation ApprovePlan($planId: ID!, $approvedByPatient: Boolean) {
    approveTreatmentPlan(planId: $planId, approvedByPatient: $approvedByPatient) {
      ${PLAN_FIELDS}
    }
  }
`;

// ── What was actually DONE ──────────────────────────────────────────────────
//
// A plan is a quote; a record is the work. They are different rows for the
// reason «انجام‌شده» was never trustworthy as a status: a status says the work
// happened and nothing else — no who, no when, no visit, no surfaces, no
// material, no note — and it can be taken back without a trace.
//
// NO AMOUNTS here, and the absence is the design (typeDefs/plan.js): the
// billable act is an order in clinicalencounter and `orderId` is the only
// thread to it, so nothing on this screen can invent what a treatment cost.
const RECORD_FIELDS = `
  id appointmentId planId planItemId catalogId
  serviceName applicability toothFdi surfaces material note
  outcome sessionNo performerId performerType actorId
  performedAtMs recordedAtMs
  orderId billingState stockState
  voidedAtMs voidedBy voidReason
`;

// The PATIENT's treatments, not this visit's — one read answering two
// questions. «جلسهٔ ۲ از ۳» counts sessions of a course that spans visits, and
// «انجام‌شده در این ویزیت» is the same list narrowed by appointment in the
// component. Asking twice would be two reads that can disagree.
//
// Voided rows come back (the default): a struck-out treatment is the row a
// dispute turns on, and `sessionsOf` already declines to count one.
export const TREATMENT_RECORDS_QUERY = `
  query TreatmentRecords($patientId: ID!, $clinicId: ID) {
    treatmentRecords(patientId: $patientId, clinicId: $clinicId) { ${RECORD_FIELDS} }
  }
`;

export const RECORD_TREATMENT = `
  mutation RecordTreatment($patientId: ID!, $clinicId: ID, $input: RecordTreatmentInput!) {
    recordTreatment(patientId: $patientId, clinicId: $clinicId, input: $input) { ${RECORD_FIELDS} }
  }
`;

// Voids, never deletes. A reason is required by the service, and by the form.
export const VOID_TREATMENT_RECORD = `
  mutation VoidTreatmentRecord(
    $patientId: ID!, $recordId: ID!, $reason: String!, $voidChartEvents: Boolean
  ) {
    voidTreatmentRecord(
      patientId: $patientId, recordId: $recordId, reason: $reason,
      voidChartEvents: $voidChartEvents
    ) {
      ${RECORD_FIELDS}
      chargeReversed
    }
  }
`;

// ── Left column: دارو ───────────────────────────────────────────────────────
//
// What was prescribed IN THIS VISIT, from the server. It is a separate read
// from the patient's medication list on `VISIT_QUERY` because the two answer
// different questions — "what did we prescribe today" and "what is this patient
// on" — and only the first one is scoped to the appointment.
export const VISIT_MEDICATIONS_QUERY = `
  query VisitMedications($patientId: ID!, $appointmentId: ID) {
    listMedications(patientId: $patientId, appointmentId: $appointmentId) {
      id code display status authoredOn dosageInstruction
    }
  }
`;

// COSTS PARSINA CREDITS. «پیشنهاد هوش مصنوعی» on the دارو tab — fired from the
// button and nowhere else, never from the interaction sweep's debounce.
//
// What comes back is a NAME the model chose, not a formulary row: adding one
// looks it up in `listDrugs` first, because the interaction sweep keys on
// `Drug.id` slugs and a synthesized id sweeps clean.
export const MEDICATION_SUGGESTIONS_QUERY = `
  query MedicationSuggestions($patientId: ID!, $appointmentId: ID!) {
    medicationSuggestions(patientId: $patientId, appointmentId: $appointmentId) {
      followUpTimeline
      items { name dosage frequency duration notes }
    }
  }
`;

/** The human dosage line off a SAVED medication's stored instruction. */
export function savedDosageText(med) {
  const d = med?.dosageInstruction;
  if (!d) return '';
  if (typeof d === 'string') return d;
  if (d.text) return String(d.text);
  return [d.dose, d.frequency, d.duration, d.note]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .join(' · ');
}

// ── Left column: دستورات ────────────────────────────────────────────────────

const ORDER_FIELDS = `
  id orderType description urgency status notes code catalogId sessionCount createdAt
`;

export const ORDER_CATALOG_QUERY = `
  query DentalOrderCatalog($orderType: String, $clinicId: ID) {
    dentalOrderCatalog(orderType: $orderType, clinicId: $clinicId) {
      id orderType name category code modality bodyPart source
    }
  }
`;

// COSTS PARSINA CREDITS. «پیشنهاد هوش مصنوعی» on the دستورات tab.
//
// Every item already carries a `catalogId` from the SAME catalogue the manual
// form picks from — the BFF drops anything its dental list cannot name — so
// «افزودن» is `createVisitOrder` with the row's own values and nothing invented
// on this side. `droppedCount` is how many the BFF refused, which is the
// difference between «پیشنهادی نیست» and «پیشنهاد بود، دندانی نبود».
export const VISIT_ORDER_SUGGESTIONS_QUERY = `
  query VisitOrderSuggestions($patientId: ID!, $appointmentId: ID!) {
    visitOrderSuggestions(patientId: $patientId, appointmentId: $appointmentId) {
      rationale
      droppedCount
      items { key orderType name catalogId reason urgency category code }
    }
  }
`;

export const VISIT_ORDERS_QUERY = `
  query VisitOrders($appointmentId: ID!) {
    visitOrders(appointmentId: $appointmentId) { ${ORDER_FIELDS} }
  }
`;

export const CREATE_VISIT_ORDER = `
  mutation CreateVisitOrder(
    $patientId: ID!, $appointmentId: ID!, $orderType: String!, $description: String!,
    $catalogId: String, $urgency: String, $notes: String, $code: String, $sessionCount: Int
  ) {
    createVisitOrder(
      patientId: $patientId, appointmentId: $appointmentId, orderType: $orderType,
      description: $description, catalogId: $catalogId, urgency: $urgency,
      notes: $notes, code: $code, sessionCount: $sessionCount
    ) { ${ORDER_FIELDS} }
  }
`;

export const SET_ORDER_STATUS = `
  mutation SetVisitOrderStatus($appointmentId: ID!, $orderId: ID!, $transition: String!) {
    setVisitOrderStatus(appointmentId: $appointmentId, orderId: $orderId, transition: $transition) {
      ${ORDER_FIELDS}
    }
  }
`;

// ── Left column: تشخیص ─────────────────────────────────────────────────────

const DIAGNOSIS_FIELDS = `
  id code display clinicalStatus verificationStatus onsetDate createdAt aiAssisted
  evidence { kind detail ref }
`;

export const VISIT_DIAGNOSES_QUERY = `
  query VisitDiagnoses($patientId: ID!, $appointmentId: ID!) {
    visitDiagnoses(patientId: $patientId, appointmentId: $appointmentId) { ${DIAGNOSIS_FIELDS} }
  }
`;

// COSTS PARSINA CREDITS. Fired only from «دریافت پیشنهاد هوش مصنوعی», never on
// load — see the note at the top of the BFF's typeDefs/visit.js.
export const VISIT_DIFFERENTIAL_QUERY = `
  query VisitDifferential($patientId: ID!, $appointmentId: ID!) {
    visitDifferential(patientId: $patientId, appointmentId: $appointmentId) {
      diagnosisCode diagnosisName supportingEvidence contradictingEvidence confidence
    }
  }
`;

export const RECORD_DIAGNOSIS = `
  mutation RecordVisitDiagnosis(
    $patientId: ID!, $appointmentId: ID!, $code: String!, $display: String!,
    $verificationStatus: String, $evidence: [DiagnosisEvidenceInput!], $aiAssisted: Boolean
  ) {
    recordVisitDiagnosis(
      patientId: $patientId, appointmentId: $appointmentId, code: $code, display: $display,
      verificationStatus: $verificationStatus, evidence: $evidence, aiAssisted: $aiAssisted
    ) { ${DIAGNOSIS_FIELDS} }
  }
`;

export const DELETE_DIAGNOSIS = `
  mutation DeleteVisitDiagnosis($patientId: ID!, $diagnosisId: ID!) {
    deleteVisitDiagnosis(patientId: $patientId, diagnosisId: $diagnosisId)
  }
`;

// ── Left column: پرسش‌ها ────────────────────────────────────────────────────

// COSTS PARSINA CREDITS.
export const VISIT_QUESTIONS_QUERY = `
  query VisitQuestions(
    $patientId: ID!, $appointmentId: ID!, $diagnosisCodes: [String!], $diagnosisNames: [String!]
  ) {
    visitQuestions(
      patientId: $patientId, appointmentId: $appointmentId,
      diagnosisCodes: $diagnosisCodes, diagnosisNames: $diagnosisNames
    ) {
      fallback
      items {
        diagnosisCode diagnosisName
        questions { id question hint positiveStatement negativeStatement }
      }
    }
  }
`;

// COSTS PARSINA CREDITS.
export const ASK_VISIT_QUESTION = `
  mutation AskVisitQuestion($patientId: ID!, $appointmentId: ID!, $question: String!) {
    askVisitQuestion(patientId: $patientId, appointmentId: $appointmentId, question: $question) {
      answer confidence references followUps
    }
  }
`;

// ── Left column: دستیار شرح حال (تشخیص tab) ────────────────────────────────
//
// The doctor app's SOAP assistant: the dentist talks (browser dictation) or
// types the visit narrative, the assistant drafts the note sections and then
// advises. Both COST PARSINA CREDITS and are fired only from the button —
// the refine bills only when it actually produced new content.

export const REFINE_VISIT_NOTE = `
  mutation RefineVisitNote($patientId: ID!, $appointmentId: ID!, $transcript: String!) {
    refineVisitNote(patientId: $patientId, appointmentId: $appointmentId, transcript: $transcript) {
      sections { section text changed additions }
      changed charged status
    }
  }
`;

// The third read of the same narrative. Separate from the two above because it
// bills separately and can fail on its own: a chart the assistant could not
// read must not cost the dentist the note draft they already got.
export const EXTRACT_TOOTH_FINDINGS = `
  mutation ExtractToothFindings($patientId: ID!, $appointmentId: ID!, $transcript: String!) {
    extractToothFindings(patientId: $patientId, appointmentId: $appointmentId, transcript: $transcript) {
      status charged
      findings { toothFdi aspect surface value material evidence confidence isHistorical }
    }
  }
`;

export const GENERATE_VISIT_ADVICE = `
  mutation GenerateVisitAdvice($patientId: ID!, $appointmentId: ID!, $transcript: String!) {
    generateVisitAdvice(patientId: $patientId, appointmentId: $appointmentId, transcript: $transcript) {
      summary suggestedDiagnosis icdCodes { code description }
      suggestedActions suggestedMedications
    }
  }
`;

/**
 * The saveVisitNote patch that APPLIES an assistant draft.
 *
 * Shared (and pure) because it encodes a rule both surfaces must agree on:
 * O/A/P are replaced with the merged text — nothing else in this app writes
 * those three fields — but S is NEVER replaced. The dentist's subjective is a
 * STRUCTURED record (chiefComplaint, painScore, teethFdi…) that a prose string
 * cannot round-trip, so the S section contributes only its `additions`,
 * appended under the dentist's own notes — the same append the پرسش‌ها tab
 * uses for a confirmed finding.
 */
export function draftToNotePatch(draft, currentNotes = '') {
  const patch = {};
  for (const s of draft?.sections || []) {
    if (!s?.changed) continue;
    if (s.section === 'o') patch.objective = s.text;
    else if (s.section === 'a') patch.assessment = s.text;
    else if (s.section === 'p') patch.plan = s.text;
    else if (s.section === 's' && s.additions?.length) {
      const added = s.additions.join('\n');
      patch.subjective = { notes: currentNotes ? `${currentNotes}\n${added}` : added };
    }
  }
  return patch;
}

/**
 * An assistant finding → the recordToothEvents input.
 *
 * Pure and shared for draftToNotePatch's reason: both surfaces must record the
 * SAME thing from the same suggestion. The mapping is deliberately narrow —
 * only the fields the chart's event grammar defines cross over, and
 * `confidence` and `evidence` are NOT among them: confidence is a UI signal for
 * whether to tick the row, and the evidence goes into `note` so the chart keeps
 * the dentist's own sentence next to the fact rather than losing the reason.
 *
 * `surface` is passed only for a surface event. Sending one on a whole-tooth
 * aspect is exactly what the dental service rejects, and it is the shape of
 * mistake a model makes.
 */
export function findingToToothEvent(finding) {
  if (!finding?.toothFdi || !finding?.aspect || !finding?.value) return null;
  return {
    toothFdi: finding.toothFdi,
    aspect: finding.aspect,
    surface: finding.aspect === 'surface' ? (finding.surface || null) : null,
    value: finding.value,
    material: finding.material || null,
    note: finding.evidence || null,
    isHistorical: !!finding.isHistorical,
  };
}

// ── Left column: فایل‌ها ────────────────────────────────────────────────────

const ANNOTATION_FIELDS = `
  id kind x y w h x2 y2 points toothFdi comment color authorId createdAtMs
`;

const FINDING_FIELDS = `
  id toothFdi aspect surface value label evidence confidence isHistorical x y w h
`;

const FILE_FIELDS = `
  id fileName fileType fileSize createdAt documentType reportText
  abnormalFlags storageFileId teethFdi fromThisVisit
  imageLinkId viewType aiReadAtMs
  annotations { ${ANNOTATION_FIELDS} }
  aiFindings { ${FINDING_FIELDS} }
`;

export const VISIT_FILES_QUERY = `
  query VisitFiles($patientId: ID!, $appointmentId: ID!) {
    visitFiles(patientId: $patientId, appointmentId: $appointmentId) { ${FILE_FIELDS} }
  }
`;

export const VISIT_FILE_URL_QUERY = `
  query VisitFileUrl($patientId: ID!, $fileId: ID!) {
    visitFileUrl(patientId: $patientId, fileId: $fileId)
  }
`;

export const UPLOAD_VISIT_FILE = `
  mutation UploadVisitFile(
    $patientId: ID!, $appointmentId: ID!, $fileName: String!, $contentType: String!,
    $fileData: String!, $documentType: String, $viewType: String, $teethFdi: [String!]
  ) {
    uploadVisitFile(
      patientId: $patientId, appointmentId: $appointmentId, fileName: $fileName,
      contentType: $contentType, fileData: $fileData, documentType: $documentType,
      viewType: $viewType, teethFdi: $teethFdi
    ) { ${FILE_FIELDS} }
  }
`;

// `runOcr` is deliberately NOT a variable here. Upload stores bytes and nothing
// else; extraction is the mutation below, taken per file and on purpose.
export const RUN_FILE_OCR = `
  mutation RunFileOcr(
    $patientId: ID!, $appointmentId: ID!, $fileId: ID!, $documentType: String
  ) {
    runFileOcr(
      patientId: $patientId, appointmentId: $appointmentId,
      fileId: $fileId, documentType: $documentType
    ) { ${FILE_FIELDS} }
  }
`;

export const REGISTER_VISIT_IMAGE = `
  mutation RegisterVisitImage(
    $patientId: ID!, $appointmentId: ID!, $fileId: ID!,
    $viewType: String!, $teethFdi: [String!]
  ) {
    registerVisitImage(
      patientId: $patientId, appointmentId: $appointmentId,
      fileId: $fileId, viewType: $viewType, teethFdi: $teethFdi
    ) { ${FILE_FIELDS} }
  }
`;

export const ANALYZE_VISIT_IMAGE = `
  mutation AnalyzeVisitImage($patientId: ID!, $appointmentId: ID!, $fileId: ID!) {
    analyzeVisitImage(
      patientId: $patientId, appointmentId: $appointmentId, fileId: $fileId
    ) {
      charged status aiReadAtMs
      findings { ${FINDING_FIELDS} }
    }
  }
`;

export const SAVE_IMAGE_ANNOTATIONS = `
  mutation SaveImageAnnotations(
    $patientId: ID!, $appointmentId: ID!, $fileId: ID!,
    $annotations: [ImageAnnotationInput!]!
  ) {
    saveImageAnnotations(
      patientId: $patientId, appointmentId: $appointmentId,
      fileId: $fileId, annotations: $annotations
    ) { ${ANNOTATION_FIELDS} }
  }
`;

export const DELETE_VISIT_FILE = `
  mutation DeleteVisitFile($patientId: ID!, $fileId: ID!) {
    deleteVisitFile(patientId: $patientId, fileId: $fileId)
  }
`;

// ── Vocabularies, shared so the two surfaces cannot label a state differently ─

/** What may be ordered. The BFF refuses anything else. */
// `dental_lab`, not `lab`: clinicalencounter reserves `lab` for a clinical
// laboratory test with a LOINC code. Dental laboratory work — a crown, a
// denture — is its own catalog type there and in the BFF's DENTAL_ORDER_TYPES.
export const ORDER_TYPES = ['imaging', 'dental_lab'];

export const ORDER_URGENCIES = ['routine', 'urgent', 'stat'];

/**
 * §3.1.3 asks for "Pending, Scheduled, Completed". The dental service's actual
 * item vocabulary is richer and is the one that gets STORED — mapping the
 * document's three words onto five real states rather than the reverse, because
 * a status the service will not accept is a button that always fails.
 *
 *   proposed  → پیشنهادشده   (the document's "Pending")
 *   accepted  → پذیرفته‌شده   (the document's "Scheduled" — agreed, not yet done)
 *   done      → انجام‌شده     (the document's "Completed")
 *   rejected  → رد شده       — the patient declined; part of what they were quoted
 *   deferred  → به تعویق      — agreed in principle, not now
 */
export const PLAN_ITEM_STATUSES = ['proposed', 'accepted', 'done', 'rejected', 'deferred'];

/**
 * What a line may become NEXT — a process, not a set of interchangeable labels.
 *
 * The dropdown used to offer every status but the current one, in both
 * directions, which read as «this control does nothing»: a dentist could put a
 * line back to پیشنهادشده after agreeing it, or mark it رد شده and un-reject it,
 * forever, with no consequence anywhere. A quote is a conversation with a
 * patient and it moves one way.
 *
 *   پیشنهادشده  we suggested it        → پذیرفته‌شده · رد شده · به تعویق
 *   پذیرفته‌شده  the patient agreed     → به تعویق · رد شده
 *   به تعویق     agreed, not now        → پذیرفته‌شده · رد شده
 *   رد شده       the patient declined   → (nothing — reopen by re-quoting)
 *   انجام‌شده    it happened            → (nothing — void the record instead)
 *
 * ── «انجام‌شده» is never in this list ──────────────────────────────────────
 * A line becomes done by RECORDING the treatment that finished it. Setting it
 * from a dropdown is the bare status overwrite this whole feature exists to
 * replace: no who, no when, no visit, no surfaces, no material, no note, and
 * reversible without a trace.
 *
 * Nor is رد شده reversible here. Undoing a refusal by picking a word from a
 * menu loses the only thing that mattered about it — that the patient was asked
 * and said no. Re-quoting is how a declined treatment comes back, and it leaves
 * the refusal in the record where it belongs.
 */
const PLAN_ITEM_TRANSITIONS = {
  proposed: ['accepted', 'deferred', 'rejected'],
  accepted: ['deferred', 'rejected'],
  deferred: ['accepted', 'rejected'],
  rejected: [],
  done: [],
};

export function nextPlanItemStatuses(current) {
  return PLAN_ITEM_TRANSITIONS[current] || [];
}

/**
 * Is this line finished with — nothing more for the dentist to decide?
 *
 * A done line happened; a rejected one was declined. Neither has a next step,
 * so neither gets a control that implies one.
 */
export const isTerminalPlanItem = (status) => !nextPlanItemStatuses(status).length;

/** The visit's own status → whether the screen may be written to. */
export const isVisitEditable = (visit) => visit?.editable !== false;

/**
 * Teeth a plan touches, as a lookup for the odontogram overlay.
 *
 * §3.1.1 asks for "real-time status updates when treatments are planned or
 * completed" and §3.1.3 for treatments "linked directly to specific teeth on
 * the Odontogram". This is that link: FDI → the item statuses planned on it.
 * Whole-mouth items (جرم‌گیری, an OPG) carry no tooth and are deliberately
 * absent rather than being assigned an arbitrary one.
 */
export function planTeethIndex(plans = []) {
  const byTooth = new Map();
  for (const plan of plans) {
    // An abandoned course is not a plan for this tooth any more.
    if (plan?.status === 'abandoned') continue;
    for (const item of plan?.items || []) {
      if (!item?.toothFdi) continue;
      if (!byTooth.has(item.toothFdi)) byTooth.set(item.toothFdi, []);
      byTooth.get(item.toothFdi).push({
        planId: plan.id,
        itemId: item.id,
        status: item.status,
        description: item.description || '',
      });
    }
  }
  return byTooth;
}

/**
 * Teeth work has been RECORDED on — «درمان‌شده», as the chart's second overlay.
 *
 * The counterpart to `planTeethIndex`: that one says what is INTENDED on a
 * tooth, this says what was DONE to it. A dentist could record a treatment and
 * see no sign of it on the odontogram at all — the chart drew the plan's dashed
 * ring and nothing else, so «چه کاری روی این دندان انجام شده؟» could only be
 * answered by leaving the chart.
 *
 * ── Live records only ───────────────────────────────────────────────────────
 * A voided treatment leaves NO mark on the arch. It stays in the tooth's
 * history, struck through with its reason — which is where a correction
 * belongs — but a badge saying «درمان‌شده» over work that was taken back is the
 * chart asserting something untrue.
 *
 * ── Whole-mouth treatments are not here ─────────────────────────────────────
 * A جرم‌گیری has no tooth. Marking all 32 would bury the teeth that actually had
 * work under a wash of identical badges, so they are listed separately (see
 * `wholeMouthRecords`) beneath the chart.
 *
 * @returns Map<fdi, { count, lastAtMs, names: string[] }>
 */
export function treatedByFdi(records = [], catalog = []) {
  const byTooth = new Map();
  // Oldest first, so `names` reads in the order the work happened and
  // `lastAtMs` ends up on the newest.
  const ordered = [...records].sort(
    (a, b) => (Number(a?.recordedAtMs) || 0) - (Number(b?.recordedAtMs) || 0),
  );
  for (const r of ordered) {
    if (!r || r.voidedAtMs || !r.toothFdi) continue;
    const at = Number(r.recordedAtMs) || 0;
    const entry = byTooth.get(r.toothFdi) || { count: 0, lastAtMs: 0, names: [] };
    entry.count += 1;
    if (at > entry.lastAtMs) entry.lastAtMs = at;
    entry.names.push(recordTitle(r, catalog));
    byTooth.set(r.toothFdi, entry);
  }
  return byTooth;
}

/**
 * Treatments that belong to no tooth — جرم‌گیری, فلوراید تراپی, a whole-mouth
 * scaling. Newest first.
 *
 * They are real work and must be visible somewhere; the chart is not that
 * somewhere (see `treatedByFdi`), so the screens render them as one line
 * beneath it.
 */
export function wholeMouthRecords(records = []) {
  return records
    .filter((r) => r && !r.voidedAtMs && !r.toothFdi)
    .sort((a, b) => (Number(b?.recordedAtMs) || 0) - (Number(a?.recordedAtMs) || 0));
}

/**
 * A plan item that is still OUTSTANDING — something the tooth is waiting for.
 *
 * `done` and `rejected` are terminal: the work happened, or the patient said
 * no. Neither is a pending intention, and marking the tooth for either leaves
 * a "planned" ring on a finished tooth forever.
 */
export const isOutstandingPlanItem = (status) =>
  !['done', 'rejected'].includes(String(status || ''));

/**
 * The teeth the arch should MARK: those with outstanding work.
 *
 * Separate from `planTeethIndex`, which is the full per-tooth list the inline
 * strip reads. §3.1.1 asks the chart to update "when treatments are planned or
 * completed" — completing the last item on a tooth clears its mark, which is
 * the visible half of that requirement.
 */
export function outstandingPlanTeeth(planTeeth) {
  const out = new Set();
  for (const [fdi, items] of planTeeth) {
    if (items.some((i) => isOutstandingPlanItem(i.status))) out.add(fdi);
  }
  return out;
}

/**
 * The plan a dentist is working on: the newest one that is not finished.
 *
 * `treatmentPlans` returns newest course first (the service's order, passed
 * through), so the first open plan IS the current one. Null means this patient
 * has no open plan and the screen offers to start one.
 */
export function activePlan(plans = []) {
  return plans.find((p) => !['completed', 'abandoned'].includes(p?.status)) || null;
}

/** A plan that has been signed is frozen — the price lock. */
export const isPlanLocked = (plan) => !!plan?.approvedAtMs;

/**
 * «کاربرد» — what part of the mouth a service treats.
 *
 * The same five words the clinic app writes onto a catalogue row, and the same
 * vocabulary `ALLOWED_APPLICABILITIES` enforces in clinicalencounter. Listed
 * here because «خدمت جدید» lets a dentist answer the question from the chair.
 *
 * `''` is «مشخص نشده» and a real choice: the plan form then asks for a tooth
 * and lets it be left blank, which is what every service did before the field
 * existed.
 */
export const APPLICABILITIES = (t) => ([
  { value: '', label: t('visit.plan.applicability.unset') },
  { value: 'tooth', label: t('visit.plan.applicability.tooth') },
  { value: 'surfaces', label: t('visit.plan.applicability.surfaces') },
  { value: 'quadrant', label: t('visit.plan.applicability.quadrant') },
  { value: 'arch', label: t('visit.plan.applicability.arch') },
  { value: 'mouth', label: t('visit.plan.applicability.mouth') },
]);

/**
 * «اثر روی چارت» for «خدمت جدید» — what a clinic-invented service does to a
 * tooth.
 *
 * The same vocabulary `chartEffectOptions` offers when RECORDING one, minus the
 * narrowing by tooth: this form is defining a service, not treating a patient,
 * so there is no tooth yet to say which surfaces exist.
 *
 * `''` is «مشخص نشده» and the default: the row then says nothing, and «ثبت
 * انجام» keeps the behaviour it always had. It is a different answer from
 * «تغییری در چارت ندهد», which is this clinic stating that the service changes
 * no tooth — worth saying out loud for a جرم‌گیری they invented themselves.
 */
export const CHART_EFFECTS = (t) => ([
  { value: '', label: t('visit.plan.applicability.unset') },
  ...chartEffectOptions('11').map((o) => ({
    value: o.value,
    label: o.layer === 'none'
      ? t('visit.record.chartEffect.none')
      : (o.layer === 'surface'
        ? t('visit.record.chartEffect.surfaceOption', { state: t(`tooth.value.${o.word}`) })
        : t(`tooth.value.${o.word}`)),
  })),
  // An incisor has no O, so the tooth above cannot offer a posterior-only
  // surface — but a service is defined for the whole mouth. The surface STATES
  // are the same list either way, which is why one representative tooth is
  // enough here and a real tooth is used at the chair.
]);

/**
 * WHICH FIELDS the add form should ask for, given the service being added.
 *
 * The catalogue states what part of the mouth a service treats («کاربرد»), and
 * until it could, this form asked for a tooth on every row — so جرم‌گیری, which
 * is not performed on a tooth, was offered one. An invented tooth on a whole-
 * mouth line ends up on a document a patient signs.
 *
 * A service that says NOTHING keeps the old behaviour exactly: ask for a tooth,
 * and let the dentist leave it blank. That is the honest answer for a clinic
 * that has not filled the field in, and for every service predating it.
 */
export function fieldsForService(entry) {
  const applicability = entry?.applicability || null;
  return {
    // «کل دهان» is offered wherever a tooth is, because a per-tooth service
    // genuinely can be planned for the whole mouth (a full-mouth extraction
    // plan is one line, not thirty-two).
    tooth: applicability !== 'mouth',
    surfaces: applicability === 'surfaces',
    // An arch or a quadrant is neither one tooth nor the whole mouth, and this
    // app has no field for either yet. Asking for a tooth would be wrong and
    // asking for nothing loses the site, so the dentist writes it in «شرح» —
    // which is what they do today for everything.
    hint: applicability === 'arch' || applicability === 'quadrant' ? applicability : null,
  };
}

/**
 * How many sessions of a line have been DONE, and how many were quoted.
 *
 * `quantity` on the line is what was quoted — a three-session root canal is one
 * line with quantity 3, not three lines (D6). The records are what say how many
 * have HAPPENED; `item.status === 'done'` is the fallback for a line finished
 * before records existed, and for one a migration marked done.
 */
export function sessionsOf(item, records = []) {
  const quoted = Math.max(1, Number(item?.quantity) || 1);
  const recorded = records.filter((r) => r?.planItemId === item?.id && !r.voidedAtMs).length;
  const done = recorded || (item?.status === 'done' ? quoted : 0);
  return { done: Math.min(done, quoted), quoted, multi: quoted > 1 };
}

/**
 * May this line be recorded against RIGHT NOW?
 *
 * Four nos, and each is a different sentence to the dentist:
 *
 *   * the visit is over            — a finished visit is read-only everywhere
 *   * the line was declined        — رد شده is the patient saying no
 *   * the line is already finished — every quoted session has a record
 *   * nothing is selected          — no line, no id to record against
 *
 * پیشنهادشده is deliberately ALLOWED. An emergency has no signed plan and no
 * agreed line, and refusing to record the work that was actually done because
 * the paperwork is behind is how work stops being recorded at all (O3). The
 * service decides what it bills at (D13); this decides only what may be typed.
 */
export function canRecordItem(item, records = [], { editable = true } = {}) {
  if (!editable || !item?.id) return false;
  if (item.status === 'rejected') return false;
  const { done, quoted } = sessionsOf(item, records);
  return done < quoted;
}

// ── «نوبت بعدی» — what the «پایان جلسه» dialog offers ──────────────────────
//
// A dentist does not think in «۲ هفتهٔ دیگر». They think «همین امروز», «فردا»,
// «تا یک هفته». So the options are the sentences they would say, and every one
// but the first two is a DEADLINE — «کمتر از یک هفته» means book within a week,
// NOT wait a week.
//
// That distinction is the whole reason a choice carries two instants:
//
//   floor  `after`  the earliest the appointment may be — the named day for
//                   «امروز»/«فردا», and NOW for every «کمتر از…»
//   ceil   `by`     the deadline, which is what the desk is actually being told
//
// Collapsing them into one would have «کمتر از یک هفته» mean «not before a
// week»: the opposite instruction, so the more urgent the dentist was the later
// the patient would be seen.
//
// `by: null` is «لازم نیست» — a real answer that raises nothing AND withdraws a
// prompt an earlier visit left, because the clinical judgement just changed.
const DAY_MS = 24 * 60 * 60 * 1000;

export const FOLLOW_UP_INTERVALS = Object.freeze([
  // A point in time: today, and today.
  { key: 'today', afterDays: 0, byDays: 0 },
  { key: 'tomorrow', afterDays: 1, byDays: 1 },
  // A window: bookable now, due by then.
  { key: 'withinWeek', afterDays: null, byDays: 7 },
  { key: 'within2Weeks', afterDays: null, byDays: 14 },
  { key: 'withinMonth', afterDays: null, byDays: 30 },
  { key: 'within3Months', afterDays: null, byDays: 90 },
  { key: 'none', afterDays: null, byDays: null },
]);

export const DEFAULT_FOLLOW_UP_INTERVAL = 'withinWeek';

/**
 * The chosen option → what `completeVisit` should be sent.
 *
 * Returns null for «the dentist was not asked», which is NOT the same as
 * «لازم نیست»: the first omits the argument and lets the service take its
 * default, the second is an explicit refusal.
 *
 * `afterDays: null` sends NO floor, so the service uses «now» — the desk may
 * book the moment the patient can come, which is what a deadline means.
 */
export function followUpInputFor(key, now = Date.now(), durationMinutes = 0) {
  const choice = FOLLOW_UP_INTERVALS.find((i) => i.key === key);
  if (!choice) return null;
  if (choice.byDays === null) return { declined: true };
  return {
    // Epoch ms as STRINGS: GraphQL Int is 32-bit and would overflow.
    ...(choice.afterDays === null
      ? {}
      : { suggestedAfterMs: String(now + choice.afterDays * DAY_MS) }),
    suggestedByMs: String(now + choice.byDays * DAY_MS),
    ...(durationMinutes > 0 ? { durationMinutes } : {}),
  };
}

export function followUpDuration(items = [], catalog = []) {
  const byId = new Map((catalog || []).filter((c) => c?.id).map((c) => [c.id, c]));
  let best = 0;
  for (const item of items) {
    const minutes = Number(byId.get(item?.catalogId)?.durationMinutes) || 0;
    if (minutes > best) best = minutes;
  }
  return best;
}

/**
 * A line that should make the dialog ask «when should they come back?».
 *
 * NOT `isOutstandingPlanItem`, and the difference is the whole point. That one
 * answers «is this tooth still waiting for something», which a PROPOSED or a
 * DEFERRED line is — both keep the odontogram's planned ring, correctly. This
 * one answers «should the desk chase an appointment», which neither is:
 *
 *   پیشنهادشده  quoted, and the patient has not answered. Chasing it turns
 *               every diagnosis a dentist types into a booking task. Removed
 *               on the product owner's instruction, 2026-09-21.
 *   به تعویق    the patient saying NOT NOW; prompting the desk anyway
 *               contradicts the thing the dentist just recorded.
 *
 * A line is chased once the dentist moves it to «پذیرفته‌شده» — the patient
 * agreed, so the desk has something to book.
 *
 * Mirrors the service's OUTSTANDING_ITEM_STATUSES exactly (its
 * app/domain/dentistry/follow_up.py); reusing the ring's rule here made the
 * dialog ask about deferred lines the service would then refuse to raise.
 */
const PROMPTS_FOR_FOLLOW_UP = ['accepted'];

/**
 * Lines the dentist is about to leave undone — what makes the dialog ask at all.
 *
 * The CLIENT's mirror of the service's rule, and used ONLY to decide whether to
 * show the section: the service decides what is actually raised, off the plan
 * and the records it holds. A dialog that asked «when should they come back?»
 * after a visit that finished everything would be asking about nothing.
 */
export function outstandingForFollowUp(plans = [], records = []) {
  const out = [];
  for (const plan of plans) {
    if (plan?.status === 'abandoned' || plan?.status === 'completed') continue;
    for (const item of plan.items || []) {
      if (!PROMPTS_FOR_FOLLOW_UP.includes(String(item?.status || ''))) continue;
      const { done, quoted } = sessionsOf(item, records);
      if (done >= quoted) continue;
      out.push(item);
    }
  }
  return out;
}

/** Clinical order, not alphabetical: an emergency line is done first whenever
 *  it was quoted. Mirrors VisitTreatmentPlan's own PHASES. */
const PHASES_IN_ORDER = ['emergency', 'phase1', 'phase2', 'maintenance'];

/**
 * WHAT THE NEXT APPOINTMENT IS FOR — the outstanding line that comes first.
 *
 * The day sheet's «بیمار بعدی» hero names the patient and the hour, and until
 * now said nothing about the work. The answer is not a field on the
 * appointment: scheduling's `visit_type` is a dead column, and the clinic desk
 * books an hour, not a treatment. What a dentist is about to do is whatever the
 * patient's plan still owes — so the next appointment's service IS the first
 * outstanding plan line, and a patient with no such line is coming for a
 * ویزیت, which the screen says in words rather than inventing a treatment.
 *
 * `outstandingForFollowUp` is the rule for «what is still owed», and it is
 * reused here deliberately: a second definition of outstanding would let the
 * hero promise work the «پایان جلسه» dialog does not chase.
 *
 * ORDERED BY PHASE, then by the plan's own ordering. The service returns items
 * in `sortOrder`, which is the order they were quoted in — and an emergency
 * line quoted last is still the one done first.
 *
 * Returns null when nothing is outstanding. `more` is how many OTHER lines are
 * waiting: the appointment is one sitting, so naming the rest would promise a
 * day's work, but hiding that there are others makes the one shown look like
 * the whole of it.
 */
export function nextPlannedService(plans = [], records = []) {
  const items = outstandingForFollowUp(plans, records);
  if (items.length === 0) return null;
  const rank = (phase) => {
    const i = PHASES_IN_ORDER.indexOf(String(phase || ''));
    return i < 0 ? PHASES_IN_ORDER.length : i;
  };
  const first = items.slice().sort((a, b) => (
    rank(a.phase) - rank(b.phase) || (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0)
  ))[0];
  return {
    label: first.description || '',
    toothFdi: first.toothFdi || null,
    more: items.length - 1,
  };
}

/** This visit's records, newest first. The audit view — voided rows included. */
export function recordsForVisit(records = [], appointmentId) {
  if (!appointmentId) return [];
  return records
    .filter((r) => r?.appointmentId === appointmentId)
    .slice()
    .sort((a, b) => (Number(b?.recordedAtMs) || 0) - (Number(a?.recordedAtMs) || 0));
}

/**
 * What to CALL a recorded treatment.
 *
 * `serviceName` is the snapshot the record was written with and is the answer
 * whenever it is there. Rows written before the dialog sent one carry only a
 * `catalogId`, and for those the catalogue's CURRENT name is a far better answer
 * than «اقدام بدون عنوان» — it may have been reworded since, which is exactly
 * why the snapshot exists, but it names the right service.
 *
 * Deliberately NOT falling back to the plan line: the service reissues item ids
 * on every write to a plan (see addItem), so a record's `planItemId` can name a
 * DIFFERENT line than the one it was recorded against — and labelling a
 * treatment with somebody else's is worse than not labelling it.
 *
 * Returns null when nothing names it, so the caller shows its own placeholder.
 */
export function recordTitle(record, catalog = []) {
  const snapshot = String(record?.serviceName || '').trim();
  if (snapshot) return snapshot;
  const id = record?.catalogId;
  const entry = id ? (catalog || []).find((c) => c?.id === id) : null;
  return entry?.name || null;
}

// ── «وضعیت دندان پس از درمان» ───────────────────────────────────────────────
//
// The gap this closes: «ثبت انجام» proposed a chart change ONLY when the
// dentist had ticked surfaces, so every treatment that touches no surface —
// عصب‌کشی, روکش, کشیدن, ایمپلنت, جرم‌گیری — was recorded, billed, and left the
// odontogram saying the tooth was untouched. The dentist's work was invisible
// on the one picture of that mouth.
//
// The catalogue is what knows: `chartEffect` on the service («عصب‌کشی مولر» ⇒
// root_canal). The form cannot guess it, and guessing from the MATERIAL was
// already rejected — zirconia is a crown, a post or an inlay.
//
// STILL A PROPOSAL. The effect prefills a control the dentist can change or
// clear; nothing is written to a chart behind them.

/** The catalogue's word for «this service changes no tooth». */
export const CHART_EFFECT_NONE = 'none';

/** «<layer>:<word>» → { layer, value }, or null for none/unparseable. */
export function parseChartEffect(effect) {
  const key = String(effect || '').trim().toLowerCase();
  if (!key || key === CHART_EFFECT_NONE) return null;
  const [layer, ...rest] = key.split(':');
  const value = rest.join(':');
  if (!value || !['surface', 'condition', 'presence'].includes(layer)) return null;
  return { layer, value };
}

/**
 * What the form should OFFER for this treatment, before the dentist touches it.
 *
 * Two rules on top of the catalogue's own answer:
 *
 *   * C4 — a session that does not finish the work proposes NOTHING. Session 1
 *     of a three-session root canal is not a root-canalled tooth, and a chart
 *     that said so would be two visits early.
 *   * A catalogue row that says NOTHING (null, the commonest value there is)
 *     falls back to the behaviour this form has always had: a restoration on
 *     whatever surfaces were ticked. That keeps every clinic-invented service
 *     working exactly as it did before the column existed.
 */
export function chartEffectFor(entry, outcome) {
  if (outcome === 'in_progress') return CHART_EFFECT_NONE;
  const declared = String(entry?.chartEffect || '').trim().toLowerCase();
  if (declared) return declared;
  return `surface:${SURFACE_STATE.RESTORATION}`;
}

/**
 * The effects this tooth can carry, for the form's picker.
 *
 * Narrowed by the tooth, not by the service: an incisor has no O surface, and
 * offering a surface state for a tooth with no surfaces ticked would be a
 * proposal that writes nothing. The vocabulary is the chart's own — the same
 * lists the tooth editor offers — so a proposal is always something the
 * odontogram can actually draw.
 */
export function chartEffectOptions(toothFdi) {
  const out = [{ value: CHART_EFFECT_NONE, layer: 'none' }];
  if (!toothFdi) return out;
  if (surfacesFor(toothFdi).length) {
    for (const state of SURFACE_STATE_OPTIONS) {
      out.push({ value: `surface:${state}`, layer: 'surface', word: state });
    }
  }
  for (const condition of CONDITION_OPTIONS) {
    out.push({ value: `condition:${condition}`, layer: 'condition', word: condition });
  }
  // Only extraction. The other absences — exfoliated, congenitally absent —
  // are not things a treatment DOES.
  out.push({ value: `presence:${PRESENCE.EXTRACTED}`, layer: 'presence', word: PRESENCE.EXTRACTED });
  return out;
}

/**
 * The chart change a treatment caused, as the form OFFERS it.
 *
 * A proposal, never a derivation. Deciding that «a composite restoration means
 * these three surfaces are now restored» is a clinical judgement, and a screen
 * that made it silently would leave the chart asserting something nobody
 * looked at. So this returns what the checkbox WOULD write, the form shows it
 * in words, and nothing is sent unless the dentist ticks it.
 *
 * ONE EVENT PER SURFACE. `TreatmentChartEventInput.surface` is a single code —
 * M D F L O I — so «MOD» is three events, not one carrying three letters. A
 * single event with `surface: 'MOD'` is refused by the service, and before that
 * it silently meant "the M surface", losing two thirds of the work.
 *
 * Empty when there is nothing to propose — a whole-mouth treatment changes no
 * tooth's state, and NEITHER DOES A MATERIAL ON ITS OWN. Zirconia says a crown
 * was fitted, or a post was cemented, or an inlay was seated; the form does not
 * know which, and guessing «crown» would put a crown on a chart because the
 * dentist typed a material — which is why the SERVICE says what it does, and
 * the material only ever decorates it.
 *
 * `effect` is what the dentist confirmed in «وضعیت دندان پس از درمان». It
 * defaults to a restoration on the ticked surfaces, which is what this function
 * did when a surface was the only chart change it could express.
 *
 * ONE EVENT PER SURFACE for a surface effect; ONE EVENT for a whole-tooth one.
 * A surface effect with nothing ticked proposes nothing at all — the same
 * silence as before, and the reason `chartEffectOptions` offers surface states
 * only on a tooth that has surfaces.
 */
export function chartEventsFor({
  toothFdi, surfaces, material, effect = `surface:${SURFACE_STATE.RESTORATION}`,
} = {}) {
  if (!toothFdi) return [];
  const parsed = parseChartEffect(effect);
  if (!parsed) return [];

  if (parsed.layer === 'surface') {
    // The surfaces THIS tooth has, not merely the six letters that exist: an
    // incisor has no O. The service refuses an event naming a surface the tooth
    // cannot carry — and it refuses the WHOLE act, record included — so a stale
    // «O» carried over from a molar's plan line would lose the treatment, not
    // just the chart change.
    const available = surfacesFor(toothFdi);
    const codes = String(surfaces || '')
      .toUpperCase()
      .split('')
      .filter((c) => Object.prototype.hasOwnProperty.call(SURFACES, c))
      .filter((c) => available.includes(c));
    return [...new Set(codes)].map((surface) => ({
      toothFdi,
      aspect: 'surface',
      surface,
      value: parsed.value,
      material: material || null,
    }));
  }

  return [{
    toothFdi,
    aspect: parsed.layer,
    surface: null,
    value: parsed.value,
    // A presence event carries no material: a tooth that has left the mouth is
    // not made of anything.
    material: parsed.layer === 'condition' ? (material || null) : null,
  }];
}

// ── Money: one currency per plan ────────────────────────────────────────────
//
// Every amount below is an integer number of MINOR UNITS of a currency — whole
// rials for IRR, fils for AED (docs/clinic-currency/CONTRACTS.md). A plan is
// counted in ONE currency (`TreatmentPlan.currency`); a catalogue entry in its
// declaration's own (`DentalTreatmentCatalogEntry.currency`). A blank or absent
// currency is IRR, which is how every plan and tariff read before currencies
// existed — and every test fixture without one — still reads exactly as it did.
//
// There is no exchange rate anywhere in Parsina. A tariff in another currency
// than its plan is NEVER priced into it: the line says so instead of showing a
// converted — or, worse, an unconverted — number, and approval would refuse it
// (CURRENCY_MISMATCH) anyway. Totals are only ever added within one currency.

/** What every amount on this plan is counted in. Blank reads IRR. */
export function planCurrencyOf(plan) {
  return normalizeCurrency(plan?.currency);
}

/**
 * A catalogue entry's tariff, as a quote in `currency` would read it.
 *
 *   { amount, currency, mismatch }
 *
 * `amount` is minor units of the ENTRY's currency, or null for «بدون تعرفه».
 * `mismatch` is true when `currency` is given and the entry is priced in
 * another one — the amount is then withheld (null), because showing it beside a
 * plan in a different currency invites exactly the sum that must never happen.
 * Omit `currency` to read the entry in its own currency (no plan to fit yet).
 */
export function entryPrice(entry, currency) {
  const own = normalizeCurrency(entry?.currency);
  const unit = Number(entry?.tariffRial) || 0;
  if (currency != null && own !== normalizeCurrency(currency)) {
    return { amount: null, currency: own, mismatch: !!entry };
  }
  return { amount: unit || null, currency: own, mismatch: false };
}

/**
 * What a line costs: the locked amount once signed, the catalogue's tariff
 * before that — and in what.
 *
 *   { amount, currency, mismatch, entryCurrency }
 *
 * `currency` is the PLAN's (pass `planCurrencyOf(plan)`); `amount` is minor
 * units of it, or null. Null is «بدون تعرفه» — a service the clinic has not
 * priced. That is a real state (D15): the treatment may still be planned and
 * performed, and accounting declines to bill a zero line. Showing «۰ تومان»
 * would say the work is free, which is a different and wrong claim.
 *
 * Before signing, a catalogue entry priced in another currency than the plan's
 * is `mismatch` with a null amount: never converted, never priced into it.
 */
export function linePrice(item, catalog = [], { locked, currency } = {}) {
  const planCurrency = normalizeCurrency(currency);
  if (locked) {
    const amount = Number(item?.lineAmountRial) || 0;
    return {
      amount: amount > 0 ? amount : null, currency: planCurrency, mismatch: false, entryCurrency: null,
    };
  }
  const entry = catalog.find((c) => c.id === item?.catalogId);
  const price = entryPrice(entry, planCurrency);
  if (price.mismatch) {
    return { amount: null, currency: planCurrency, mismatch: true, entryCurrency: price.currency };
  }
  return {
    amount: price.amount ? price.amount * Math.max(1, Number(item?.quantity) || 1) : null,
    currency: planCurrency,
    mismatch: false,
    entryCurrency: price.currency,
  };
}

/**
 * The line's amount alone, in minor units of `currency` (the plan's), or null.
 * See `linePrice`; kept because it is the question most callers ask.
 */
export function lineTariffRial(item, catalog = [], { locked, currency } = {}) {
  return linePrice(item, catalog, { locked, currency }).amount;
}

/**
 * What the OPEN lines of a plan still add up to — «مانده از پلن» — and in what:
 * `{ amount, currency }`, `currency` being the plan's. Every line `linePrice`
 * prices is in that currency by construction, so this is a sum in ONE currency;
 * a line priced in another is not in it at all.
 */
export function outstandingTotal(plan, catalog = []) {
  const locked = isPlanLocked(plan);
  const currency = planCurrencyOf(plan);
  const amount = (plan?.items || [])
    .filter((i) => isOutstandingPlanItem(i.status))
    .reduce((sum, i) => sum + (linePrice(i, catalog, { locked, currency }).amount || 0), 0);
  return { amount, currency };
}

/** `outstandingTotal(...).amount` — minor units of the plan's currency. */
export function outstandingTotalRial(plan, catalog = []) {
  return outstandingTotal(plan, catalog).amount;
}

/** IRR first, then the allow-list's order — a stable order for per-currency rows. */
function byCurrencyOrder(a, b) {
  return CURRENCY_CODES.indexOf(a.currency) - CURRENCY_CODES.indexOf(b.currency);
}

/**
 * What is still owed across SEVERAL plans — the patient record's «مانده» — as
 * one total PER CURRENCY: `{ count, totals: [{ currency, amount }] }`.
 *
 * A patient can hold courses at two clinics that keep their books in different
 * currencies (`treatmentPlans` without a clinic spans them), and a toman total
 * plus a dirham total is not a number. So they are never added: each currency
 * that has an open line gets its own row, even when its lines are all unpriced
 * (a «۰ تومان» row is what this has always shown for those). `count` is lines,
 * not money, and counts across currencies. Abandoned courses owe nothing.
 */
export function outstandingByCurrency(plans = [], catalog = []) {
  let count = 0;
  const totals = new Map();
  for (const plan of plans || []) {
    if (!plan || plan.status === 'abandoned') continue;
    const locked = isPlanLocked(plan);
    const currency = planCurrencyOf(plan);
    for (const item of plan.items || []) {
      if (!isOutstandingPlanItem(item.status)) continue;
      count += 1;
      const { amount } = linePrice(item, catalog, { locked, currency });
      totals.set(currency, (totals.get(currency) || 0) + (amount || 0));
    }
  }
  return {
    count,
    totals: [...totals].map(([currency, amount]) => ({ currency, amount })).sort(byCurrencyOrder),
  };
}

/**
 * Is this plan ready to be signed?
 *
 * Not «does it have lines» — an approval freezes numbers, so what matters is
 * whether there are numbers to freeze. A plan whose every line is unpriced
 * would lock a document of zeros, which is worse than not signing: it reads as
 * «we agreed this work is free».
 *
 * And not while any line is priced in another currency than the plan's: the
 * approval would refuse the whole plan (CURRENCY_MISMATCH) rather than sign a
 * quote that adds two currencies together.
 */
export function canApprovePlan(plan, catalog = []) {
  if (!plan || isPlanLocked(plan)) return false;
  const items = plan.items || [];
  if (!items.length) return false;
  const currency = planCurrencyOf(plan);
  const prices = items.map((i) => linePrice(i, catalog, { locked: false, currency }));
  if (prices.some((p) => p.mismatch)) return false;
  return prices.some((p) => p.amount !== null);
}

/**
 * The catalogue sentence for a clinic-currency refusal, by `extensions.code`, or
 * null for anything else (the caller keeps its own message).
 *
 *   CLINIC_CURRENCY_UNAVAILABLE  the BFF could not read the clinic's currency and
 *                                will not guess one — retryable.
 *   CURRENCY_MISMATCH            two documents (a plan and a tariff, a plan and a
 *                                deposit, the clinic and its books) are in
 *                                different currencies. Not retryable from here.
 *
 * The app's own copy rather than the server's sentence, so it reads in the
 * panel's language and names no ISO codes a dentist has to decode.
 */
export function planErrorMessage(t, err) {
  switch (err?.extensions?.code) {
    case 'CLINIC_CURRENCY_UNAVAILABLE': return t('visit.plan.error.currencyUnavailable');
    case 'CURRENCY_MISMATCH': return t('visit.plan.error.currencyMismatch');
    default: return null;
  }
}

/**
 * The currency a clinic keeps its books in, as far as this screen can tell —
 * or null when it cannot. Null is an answer, not IRR: guessing IRR for an AED
 * clinic reads a typed «12.50» as toman and saves a tenth-of-a-dirham tariff.
 *
 * In order of freshness:
 *   1. An UNSIGNED plan of this clinic. The BFF resolves its currency from the
 *      clinic on every read, so it is the clinic's current one.
 *   2. The dentist's membership of the clinic (`myClinics[].clinic.currency`),
 *      read once per session. A membership whose clinic could not be named
 *      (`clinic: null`) says nothing.
 *   3. The clinic's own service list, when every entry names one currency:
 *      those are accounting's declarations for this clinic, in its books'
 *      currency.
 */
export function clinicCurrencyFor({ clinicId, plan, clinics = [], catalog = [] } = {}) {
  if (plan && !isPlanLocked(plan) && (!clinicId || plan.clinicId === clinicId)) {
    return planCurrencyOf(plan);
  }
  const membership = clinicId ? (clinics || []).find((m) => m?.clinicId === clinicId) : null;
  if (membership?.clinic) return normalizeCurrency(membership.clinic.currency);
  const codes = new Set((catalog || []).map((c) => normalizeCurrency(c?.currency)));
  return codes.size === 1 ? [...codes][0] : null;
}

/**
 * Read a File into the base64 the BFF expects.
 *
 * `readAsDataURL` and then dropping the prefix, rather than `readAsArrayBuffer`
 * and encoding by hand: the browser's encoder is the one that has to agree with
 * `Buffer.from(s, 'base64')` server-side, and a hand-rolled one over a typed
 * array is where a corrupted upload comes from.
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : '');
    };
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

/** The BFF's own ceiling, mirrored so the refusal happens before the upload. */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/** A document type the OCR path understands. */
export const DOCUMENT_TYPES = ['lab_report', 'prescription', 'general'];

/**
 * What a dental image IS, and which uploads are images at all.
 *
 * DEFINED IN `src/lib/imageMarks.js` and re-exported here, not redefined: the
 * annotation canvas, the findings layer and this screen all need the same
 * vocabulary, and the dental service refuses anything outside it. A second copy
 * would be a list that drifts from the one the server enforces — and the drift
 * shows up as an upload refused for a view type this screen offers.
 *
 * Chosen by the dentist at upload, not guessed by the model, because it decides
 * what a later reading may report at all: a bitewing does not show a periapical
 * lesion and a photograph shows no bone. One extra click buys every finding its
 * frame. CBCT is deliberately absent (CORE.md §9).
 */
export { VIEW_TYPES, isImageType } from '../../lib/imageMarks.js';

/**
 * Which documents «استخراج متن» is worth offering for.
 *
 * PDFs and photographed REPORTS — things with text in them. A radiograph is
 * not one: it has no text, so the extraction returns nothing and the dentist
 * has waited to be told so. Reading a radiograph is «خواندن تصویر», which is a
 * different question with a different answer.
 *
 * Until 2026-08-31 this matched every image/* type and ran BY DEFAULT on upload,
 * which is exactly how every periapical got sent to a transcription model.
 */
export const canExtractReport = (contentType, viewType) => {
  if (viewType && viewType !== 'photo') return false;
  return /^(application\/pdf|image\/(jpeg|png|tiff|bmp|webp))$/i.test(
    String(contentType || ''),
  );
};

/**
 * A label for a value the SERVER chose, with the raw value as the fallback.
 *
 * `t()` falls back to the KEY when a catalogue misses one, so an unmapped
 * status renders «visit.orders.statuses.on_hold» — a raw dotted string in the
 * middle of a clinical screen. Every vocabulary on this screen comes from a
 * downstream service that can add a value without this app knowing, and the
 * colour lookups beside these labels already fall back; the labels did not.
 *
 * Showing the raw value is the honest degradation: «on_hold» is at least the
 * truth about what the server said.
 */
export function labelFor(t, prefix, value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const key = `${prefix}.${raw}`;
  return t.has?.(key) ? t(key) : raw;
}

/** Human file size, formatted by the caller's locale formatter. */
export function fileSizeParts(bytes) {
  const n = Number(bytes) || 0;
  if (n <= 0) return null;
  if (n < 1024) return { value: n, unit: 'b' };
  if (n < 1024 * 1024) return { value: Math.round(n / 1024), unit: 'kb' };
  return { value: Math.round((n / (1024 * 1024)) * 10) / 10, unit: 'mb' };
}
